import { useCallback, useEffect, useState } from "react";
import { ApiError, type DialDocument, type DialDocumentRow, type DocumentBrowsePage, type DocumentBrowseSort } from "@keydials/api-client";
import { useWorkspaceServices } from "../context";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "../vocabulary";
import { Icon, type IconName } from "../components/Icon";
import { hexA, timeAgo } from "../lib/ui";
import { downloadTargetId, emptyMessage, rowDialLabel, zipRequest } from "./fileRollup";

const iconBtn = {
  border: "none",
  background: "none",
  cursor: "pointer",
  color: "var(--text-faint)",
  display: "flex",
  padding: 4,
  flex: "none",
} as const;

function fmtBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}

/** Triggers a browser save-as for an in-memory blob via a throwaway anchor click. */
function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function DocStatusPill({ status }: { status: DialDocument["status"] }) {
  const styles: Record<DialDocument["status"], { bg: string; fg: string; label: string }> = {
    Pending: { bg: hexA("#f5a51c", 0.16), fg: "#b9770e", label: "Scanning…" },
    Clean: { bg: hexA("#3eb034", 0.16), fg: "#2f8a28", label: "Clean" },
    Quarantined: { bg: hexA("#e7161b", 0.14), fg: "#c11118", label: "Quarantined" },
  };
  const s = styles[status];
  return <span style={{ borderRadius: 99, padding: "2px 8px", fontSize: 10.5, fontWeight: 700, background: s.bg, color: s.fg, whiteSpace: "nowrap" }}>{s.label}</span>;
}


/** Just enough of the dial's resolved structure to offer a scope picker. The host already holds this on
 * its view model, so narrowing the file list costs no extra request — and taking the minimum rather than
 * the whole `VSection` keeps this panel usable from anywhere that can name a section and its segments. */
export type FileScopeSection = { key: string; label: string; segments: { key: string; name: string }[] };

/** How many rows one page of the browser holds. The server clamps anything above 200. */
const PAGE_SIZE = 50;

/** How long the search box waits after the last keystroke before asking the server. Long enough that
 * typing a word is one request rather than five, short enough not to feel like a submit button. */
const SEARCH_DEBOUNCE_MS = 300;

/** The label a file carries when it was never tagged. Shown as "Other" wherever a person reads it, the
 * same word the upload picker uses for the same bucket. */
const UNTAGGED = "Uncategorized";

/** Which level of the dial a row hangs off, at a glance. "layers" is the section icon everywhere else in
 * the workspace and "clip" already means "attached file", so both read the same here as they do there. */
const LOCATION_ICON: Record<DialDocumentRow["locationKind"], IconName> = {
  Section: "layers",
  Segment: "target",
  Item: "check",
  Attribute: "clip",
};

/** What each icon means, in the words the rest of the app uses: a segment is an "action point" and an
 * attribute is a "field" everywhere a person reads them. Named once, for the row's tooltip and the key
 * under the table alike (KEY-487) — four bare glyphs with no legend was a puzzle, not a hint.
 *
 * KEY-539 turned this from a constant into a function of the dial's own vocabulary. It was always the
 * clearest statement of the segment → "action point" translation in the codebase, so it is the natural place
 * for that translation to become configurable. The other three words are the platform's everywhere. */
function locationLabels(noun: SegmentNoun): Record<DialDocumentRow["locationKind"], string> {
  return {
    Section: "Section",
    Segment: noun.One,
    Item: "Item",
    Attribute: "Field",
  };
}

/** The order the key lists the kinds in: outermost level first, the way the dial is walked. */
const LOCATION_KINDS: DialDocumentRow["locationKind"][] = ["Section", "Segment", "Item", "Attribute"];

/** The columns the server can sort on, in the order they appear. Sorting is entirely server-side: the
 * client holds one page, so a column sorted here would only ever order the rows already on screen. */
const COLUMNS: { key: DocumentBrowseSort; label: string; numeric?: boolean }[] = [
  { key: "name", label: "File" },
  { key: "location", label: "Location" },
  { key: "category", label: "Category" },
  { key: "size", label: "Size", numeric: true },
  { key: "uploaded", label: "Uploaded", numeric: true },
  { key: "status", label: "Status" },
];

/** Which way a column reads best on first click: newest and largest first, everything else A→Z. */
function defaultDirection(column: DocumentBrowseSort): "asc" | "desc" {
  return column === "uploaded" || column === "size" ? "desc" : "asc";
}

const th = { padding: "0 10px 8px", whiteSpace: "nowrap" } as const;

function SortableHeader({
  column, label, sort, dir, onSort,
}: {
  column: DocumentBrowseSort;
  label: string;
  sort: DocumentBrowseSort;
  dir: "asc" | "desc";
  onSort: (column: DocumentBrowseSort) => void;
}) {
  const active = sort === column;
  return (
    <th style={th} aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className="kd-focusable"
        onClick={() => onSort(column)}
        title={`Sort by ${label.toLowerCase()}`}
        /* A bare "File" announces as a button that says nothing about what pressing it does; the visible
           word is still inside the name, so this reads correctly and satisfies label-in-name. */
        aria-label={`Sort by ${label.toLowerCase()}`}
        style={{
          font: "inherit", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
          border: "none", background: "none", padding: 0, cursor: "pointer", display: "inline-flex",
          alignItems: "center", gap: 4, color: active ? "var(--text)" : "var(--text-faint)",
        }}
      >
        {label}
        {/* The arrow is on the active column only: a resting indicator on every header reads as six
            competing sorts rather than one. */}
        {active && <Icon name={dir === "asc" ? "chevUp" : "chevDown"} size={12} />}
      </button>
    </th>
  );
}

/** Where a file lives, in the words the dial currently uses. The labels are resolved server-side, so a
 * re-worded section reads the same here as on the dial itself; each falls back to its key on the rare row
 * the structure no longer names (an attribute file orphaned by a definition upgrade, say).
 *
 * On a group's rolled-up list (KEY-643) a subdial's row adds a "From: …" line naming that subdial: every child
 * of one group so often shares a key scheme that "Safety / Fire doors" alone would not say which site it is. */
function LocationCell({ row, noun, from }: { row: DialDocumentRow; noun: SegmentNoun; from: string | null }) {
  const locationLabel = locationLabels(noun);
  const path = [
    row.sectionLabel ?? row.sectionKey,
    row.segmentName ?? (row.segmentKey || null),
    row.itemName ?? row.itemKey,
  ].filter(Boolean) as string[];
  const attribute = row.attributeKey ? (row.attributeLabel ?? row.attributeKey) : null;

  const full = attribute ? `${path.join(" / ")} \u00b7 ${attribute}` : path.join(" / ");

  // The width lives on this DIV, not on the <td>: under `table-layout: auto` a cell's max-width is
  // ignored, so a long section name rendered at full width and spilled across the columns to its right
  // instead of ellipsing. `minWidth: 0` on the text is the other half — a flex child defaults to
  // min-width:auto and refuses to shrink below its content, which defeats the ellipsis on its own.
  const location = (
    <div style={{ display: "flex", alignItems: "center", gap: 6, maxWidth: 320, overflow: "hidden" }} title={full}>
      <span
        role="img"
        aria-label={locationLabel[row.locationKind]}
        title={`${locationLabel[row.locationKind]} file`}
        style={{ display: "inline-flex", flex: "none" }}
      >
        <Icon name={LOCATION_ICON[row.locationKind]} size={13} style={{ color: "var(--text-faint)" }} />
      </span>
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {path.join(" / ")}
        {attribute && (
          <span style={{ color: "var(--text-faint)" }}> · {attribute}</span>
        )}
      </span>
    </div>
  );
  if (!from) {
    return location;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {location}
      <span
        title={from}
        style={{ fontSize: 12, color: "var(--text-faint)", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        From: {from}
      </span>
    </div>
  );
}

/**
 * Instance-overview "Files" tab: every file across the whole dial in one sortable, paged table — as opposed
 * to <c>Documents</c> in ActionPointPanel, which only ever shows one section/segment's files. Select any
 * subset (or none, for "everything") and download as a single ZIP, alongside the per-row SAS download.
 *
 * Sorting and paging are the SERVER's (KEY-459 phase 2, `browseDocuments`), not this component's, for one
 * reason: the client holds a single page, so anything sorted here would only reorder the rows already on
 * screen and "sort by size" would mean "sort this page by size". The same call also hands back the resolved
 * display labels for each row's location, which is why the Location column can read "Fire safety / Weekly
 * checks" rather than "fs / wk".
 *
 * The rows are visibility-filtered server-side (KEY-49, KEY-468, and KEY-537's DocumentList mask): a
 * scoped-grant holder simply never receives rows for sections they can't see, and `totalCount` counts their
 * own filtered set — so no client-side filtering is needed here, and the last page is one they can actually
 * reach. A reader the dial excludes from the file list never reaches this component at all: the host drops
 * the Files tab rather than mounting an empty table.
 *
 * The tag filter and the search box (KEY-538) are server-side too, and for the same reason as the sort: the
 * client holds one page, so filtering here would only ever filter the rows already on screen and `totalCount`
 * would page the reader into blanks. Both reset to page 1, exactly as a re-sort or a re-scope does, and both
 * ride along with "Download as ZIP" so the archive matches the table beside it.
 *
 * On a Group Key Dial (`isGroup`, KEY-643) the table rolls up the group's own files and every subdial's the reader
 * can see, each row saying which subdial it came from; a dial picker replaces the section picker, since a section
 * key names a node of one dial, not of every dial in the group. Each row's download goes to its OWN dial, and every
 * ZIP carries `includeChildren` — ticked ids too — because the server otherwise looks ids up on the group alone.
 */
export function InstanceFilesPanel({
  instanceId,
  sections = [],
  initialSectionKey = null,
  canDownloadDocs = true,
  noun = DEFAULT_SEGMENT_NOUN,
  isGroup = false,
}: {
  instanceId: string;
  sections?: FileScopeSection[];
  /** This dial is a Group Key Dial: list its subdials' files alongside its own (KEY-643). Opt-in, so the section
   *  Files tab and every single dial keep the one-dial table. */
  isGroup?: boolean;
  /** This dial's word for an action point (KEY-539). This panel is mounted with a section list rather than
   *  the instance detail, so the vocabulary arrives as a prop; omitted means the platform's word. */
  noun?: SegmentNoun;
  /** Opens the browser already scoped to this section (KEY-460 part 1's section Files tab) rather than
   *  "All sections" — the reader came here for one section's files, not a reminder they can widen the view. */
  initialSectionKey?: string | null;
  /**
   * Whether this reader may download files on this dial at all (KEY-537). Gates the two ZIP buttons, which
   * no single row owns: the server refuses a zip containing anything the caller may not fetch, so offering
   * them would be offering a button that always fails. Each row's own button is gated on the server's
   * per-file answer (`row.canDownload`) instead, which is scope-aware where this flag is not.
   */
  canDownloadDocs?: boolean;
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  const [page, setPage] = useState<DocumentBrowsePage | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [sort, setSort] = useState<DocumentBrowseSort>("uploaded");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [sectionKey, setSectionKey] = useState<string | null>(initialSectionKey);
  const [segmentKey, setSegmentKey] = useState<string | null>(null);
  // KEY-643: on a group, which one of its dials the table is narrowed to (null = all of them).
  const [dialId, setDialId] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  // Two pieces of search state, not one: `search` is what the box shows (every keystroke), `query` is what
  // the server has been asked (settled). Sharing one would either re-request per keystroke or lag the
  // caret behind the typing.
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [zipping, setZipping] = useState<"selected" | "all" | null>(null);
  // KEY-539: the icon key under the table reads the dial's own word, resolved once rather than per row.
  const locationLabel = locationLabels(noun);

  // Debounced, because a request per keystroke on a dial with a lot of files is a lot of wasted work and
  // the answers can land out of order. Page 1 comes with it: "page 3 of the old results" names nothing in
  // the new ones, exactly as for a re-sort or a re-scope.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery((current) => {
        if (current === search) {
          return current;
        }
        setPageNumber(1);
        return search;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    try {
      // A group's roll-up is never section-scoped (the server refuses the combination): the dial picker is its
      // narrowing instead.
      const narrowing = isGroup ? { includeChildren: true, dialId } : { sectionKey, segmentKey };
      setPage(await api.browseDocuments(token, instanceId, { ...narrowing, sort, dir, page: pageNumber, pageSize: PAGE_SIZE, tags, q: query }));
    } catch {
      setError("Could not load the file list.");
    }
    // `tags` is an array rebuilt on every toggle, so it is spread into the dependency list by identity —
    // joined instead, so an unchanged selection doesn't re-request on an unrelated render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId, isGroup, sectionKey, segmentKey, dialId, sort, dir, pageNumber, tags.join("\u0000"), query]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Clicking the active column flips it; clicking a new one adopts that column's natural direction. Either
   *  way the paging restarts, because "page 3 of the old order" names nothing in the new one. */
  function sortBy(column: DocumentBrowseSort) {
    setDir(column === sort ? (dir === "asc" ? "desc" : "asc") : defaultDirection(column));
    setSort(column);
    setPageNumber(1);
  }

  /** Narrowing the view restarts the paging for the same reason a re-sort does — and clears the segment
   *  when the section changes, since a segment key only means anything inside its own section. */
  function scopeTo(nextSection: string | null, nextSegment: string | null) {
    setSectionKey(nextSection);
    setSegmentKey(nextSegment);
    setPageNumber(1);
  }

  /** Narrowing a group's table to one of its dials (KEY-643) — a re-scope like any other, so back to page 1. */
  function showDial(nextDial: string | null) {
    setDialId(nextDial);
    setPageNumber(1);
  }

  /** Ticking a tag on or off. Several tags OR — a file carries exactly one, so an AND of two would be an
   *  empty result nobody could ever mean. Restarts the paging for the same reason a re-scope does. */
  function toggleTag(label: string) {
    setTags((current) => (current.includes(label) ? current.filter((t) => t !== label) : [...current, label]));
    setPageNumber(1);
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  /** Selects (or clears) this page's rows, leaving any ticked on other pages alone — the selection is a
   *  running basket across pages, so paging away from a ticked row must not silently drop it from the zip. */
  function toggleAll() {
    const rows = page?.items ?? [];
    if (rows.length === 0) {
      return;
    }
    const allOnPageSelected = rows.every((r) => selected.has(r.id));
    setSelected((prev) => {
      const next = new Set(prev);
      for (const row of rows) {
        if (allOnPageSelected) {
          next.delete(row.id);
        } else {
          next.add(row.id);
        }
      }
      return next;
    });
  }

  async function download(doc: DialDocumentRow) {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    try {
      // The row's OWN dial: on a group's list it is often a subdial, and asked under the group's id it 404s.
      const { downloadUrl } = await api.getDocumentDownloadUrl(token, downloadTargetId(doc, instanceId), doc.id);
      window.open(downloadUrl, "_blank", "noopener");
    } catch {
      setError("Could not get the download link.");
    }
  }

  /**
   * Two ways to name a zip, deliberately kept apart.
   *
   * By ID: exactly the rows the reader ticked, wherever in the dial they are — so NO scope goes with it,
   * or the server would intersect the two and silently drop every ticked row outside the current view.
   *
   * By SCOPE: "everything here", sent as the intent rather than a list, because under paging the reader
   * has only ever loaded a fraction of it. The server re-derives the set through the same visibility
   * filter the table was built from (KEY-459 phase 2), so the zip and the view can't disagree.
   *
   * Which of the two, and what rides with it — including a group's `includeChildren` — is `zipRequest`'s call.
   */
  async function downloadZip(ids: string[] | undefined) {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    setZipping(ids ? "selected" : "all");
    setError(null);
    try {
      const { documentIds, scope } = zipRequest(ids, { isGroup, sectionKey, segmentKey, dialId, tags, query });
      const blob = await api.downloadDocumentsZip(token, instanceId, documentIds, scope);
      saveBlob(blob, `dial-files-${instanceId}.zip`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not build the zip.");
    } finally {
      setZipping(null);
    }
  }

  if (page === null) {
    return (
      <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
        <p style={{ color: "var(--text-faint)", fontSize: 14, margin: 0 }}>Loading files…</p>
      </div>
    );
  }

  const rows = page.items;
  const total = page.totalCount;
  const firstOnPage = total === 0 ? 0 : (page.page - 1) * page.pageSize + 1;
  const lastOnPage = firstOnPage === 0 ? 0 : firstOnPage + rows.length - 1;
  const hasMore = lastOnPage < total;
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const activeSection = isGroup ? null : (sections.find((sec) => sec.key === sectionKey) ?? null);
  const activeSegment = activeSection?.segments.find((seg) => seg.key === segmentKey) ?? null;
  const dials = page.dials ?? [];
  const activeDial = isGroup ? (dials.find((d) => d.instanceId === dialId) ?? null) : null;
  // What the whole-set download is actually going to fetch, said in the reader's own terms — "all" is a
  // lie once the view is narrowed, and the button really does send the narrower request. A tag or a search
  // narrows it just as a scope does, so "these" is the honest word once either is on: naming them all would
  // run to a sentence on a button.
  const filtered = tags.length > 0 || query !== "";
  const scopeNoun = filtered
    ? "these"
    : activeDial ? `“${activeDial.name}”`
    : activeSegment ? `“${activeSegment.name}”` : activeSection ? `“${activeSection.label}”` : "all";
  const tagOptions = page.availableTags ?? [];

  return (
    <div className="kd-card" style={{ padding: "var(--pad-card)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: "var(--text)" }}>
          Files{total ? ` · ${total}` : ""}
        </h2>
        {/* KEY-643: a group narrows by DIAL, not by section — its sections are its own face, and a section key
            names a node of one dial. The options come from the page (dials holding a file this reader can see),
            and the picker hides when there is only one, exactly as the tag filter does. */}
        {isGroup && dials.length > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>Show</span>
            <select
              className="kd-focusable"
              aria-label="Filter files by subdial"
              value={dialId ?? ""}
              onChange={(e) => showDial(e.target.value || null)}
              style={selectStyle}
            >
              <option value="">This dial and its subdials</option>
              {dials.map((d) => (
                <option key={d.instanceId} value={d.instanceId}>{d.name}</option>
              ))}
            </select>
          </div>
        )}
        {!isGroup && sections.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>Show</span>
            <select
              className="kd-focusable"
              aria-label="Filter files by section"
              value={sectionKey ?? ""}
              onChange={(e) => scopeTo(e.target.value || null, null)}
              style={selectStyle}
            >
              <option value="">All sections</option>
              {sections.map((sec) => (
                <option key={sec.key} value={sec.key}>{sec.label}</option>
              ))}
            </select>
            {activeSection && activeSection.segments.length > 0 && (
              <select
                className="kd-focusable"
                aria-label={`Filter files by ${noun.one}`}
                value={segmentKey ?? ""}
                onChange={(e) => scopeTo(sectionKey, e.target.value || null)}
                style={selectStyle}
              >
                <option value="">All {noun.many}</option>
                {activeSection.segments.map((seg) => (
                  <option key={seg.key} value={seg.key}>{seg.name}</option>
                ))}
              </select>
            )}
          </div>
        )}
        <input
          className="kd-focusable"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search files…"
          aria-label="Search files by name or tag"
          style={{
            font: "inherit", fontSize: 12.5, padding: "5px 9px", borderRadius: "var(--radius-sm)",
            border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)",
            minWidth: 170,
          }}
        />
        {/* KEY-537: both ZIP buttons go entirely when this reader may not download. The server refuses a
            zip that would contain anything they can't fetch — refuses it WHOLE, rather than quietly
            shrinking the archive — so a disabled-looking button would just be a promise it can't keep.
            Not rendered at all rather than hidden: a disabled control still says "you could do this". */}
        {canDownloadDocs && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            className="kd-focusable"
            disabled={selected.size === 0 || zipping !== null}
            onClick={() => void downloadZip([...selected])}
            style={{
              font: "inherit", fontSize: 13, fontWeight: 700, padding: "7px 13px", borderRadius: "var(--radius-sm)",
              border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)",
              cursor: selected.size === 0 || zipping !== null ? "default" : "pointer",
              opacity: selected.size === 0 ? 0.5 : 1, display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <Icon name="download" size={15} />
            {zipping === "selected" ? "Zipping…" : `Download selected (${selected.size}) as ZIP`}
          </button>
          <button
            type="button"
            className="kd-focusable"
            /* Gated on the dial holding any files at all rather than any CLEAN ones: only this page's
               statuses are in hand, and the server decides what is downloadable anyway. */
            disabled={total === 0 || zipping !== null}
            onClick={() => void downloadZip(undefined)}
            style={{
              font: "inherit", fontSize: 13, fontWeight: 700, padding: "7px 13px", borderRadius: "var(--radius-sm)",
              border: "var(--border-w) solid var(--accent-border)", background: "var(--accent-soft)", color: "var(--accent-text)",
              cursor: total === 0 || zipping !== null ? "default" : "pointer",
              opacity: total === 0 ? 0.5 : 1, display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <Icon name="download" size={15} />
            {zipping === "all" ? "Zipping…" : `Download ${scopeNoun} as ZIP`}
          </button>
        </div>
        )}
      </div>
      {/* The tag filter. Options come from `page.availableTags` — the tags actually on this caller's files
          — and NEVER from the dial's tag table or a hard-coded list: a tag can be withdrawn from the picker
          while the files filed under it keep the label for ever, and options built the other way would make
          every one of those files unreachable. A withdrawn tag simply appears here under its own name.
          Hidden entirely when a dial has only ever used one bucket: a filter with one option filters nothing. */}
      {tagOptions.length > 1 && (
        <div
          role="group"
          aria-label="Filter files by tag"
          style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 12 }}
        >
          <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>Tagged</span>
          {tagOptions.map((label) => {
            const on = tags.includes(label);
            return (
              <button
                key={label}
                type="button"
                className="kd-focusable"
                aria-pressed={on}
                onClick={() => toggleTag(label)}
                style={{
                  font: "inherit", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 99,
                  cursor: "pointer",
                  border: `var(--border-w) solid ${on ? "var(--accent-border)" : "var(--border)"}`,
                  background: on ? "var(--accent-soft)" : "var(--surface)",
                  color: on ? "var(--accent-text)" : "var(--text-muted)",
                }}
              >
                {label === UNTAGGED ? "Other" : label}
              </button>
            );
          })}
          {tags.length > 0 && (
            <button
              type="button"
              className="kd-focusable"
              onClick={() => { setTags([]); setPageNumber(1); }}
              style={{
                font: "inherit", fontSize: 12, fontWeight: 600, padding: "3px 8px", borderRadius: "var(--radius-sm)",
                border: "none", background: "none", color: "var(--text-faint)", cursor: "pointer", textDecoration: "underline",
              }}
            >
              Clear
            </button>
          )}
        </div>
      )}
      {error && <p style={{ margin: "0 0 10px", fontSize: 12.5, color: "var(--st-overdue)" }}>{error}</p>}
      {total === 0 ? (
        <p style={{ color: "var(--text-faint)", fontSize: 14, margin: 0 }}>
          {emptyMessage({
            filtered,
            isGroup,
            dialName: activeDial?.name,
            sectionLabel: activeSection?.label,
            segmentName: activeSegment?.name,
          })}
        </p>
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            {/* minWidth turns the wrapper's overflow-x into a real scroller on a narrow screen. Without it
                `width: 100%` squeezed the right-hand columns until "Quarantined" read as "Quarantine". */}
            <table style={{ width: "100%", minWidth: 880, borderCollapse: "collapse", fontSize: 13.5 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--text-faint)" }}>
                  <th style={{ ...th, width: 28 }}>
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleAll}
                      aria-label="Select all files on this page"
                      style={{ width: 15, height: 15 }}
                    />
                  </th>
                  {COLUMNS.map((c) => (
                    <SortableHeader key={c.key} column={c.key} label={c.label} sort={sort} dir={dir} onSort={sortBy} />
                  ))}
                  <th style={{ ...th, width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id} style={{ borderTop: "var(--border-w) solid var(--border)" }}>
                    <td style={{ padding: "9px 10px" }}>
                      <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)} aria-label={`Select ${d.fileName}`} style={{ width: 15, height: 15 }} />
                    </td>
                    <td style={{ padding: "9px 10px", fontWeight: 600, color: "var(--text)" }}>
                      <div style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={d.fileName}>
                        {d.fileName}
                      </div>
                    </td>
                    <td style={{ padding: "9px 10px", color: "var(--text-muted)" }}>
                      <LocationCell row={d} noun={noun} from={rowDialLabel(d, instanceId, isGroup)} />
                    </td>
                    <td style={{ padding: "9px 10px", color: "var(--text-muted)" }}>{d.category === UNTAGGED ? "—" : d.category}</td>
                    <td style={{ padding: "9px 10px", color: "var(--text-muted)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmtBytes(d.sizeBytes)}</td>
                    <td style={{ padding: "9px 10px", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                      {d.uploadedByEmail} · {timeAgo(d.uploadedAtUtc)}
                    </td>
                    <td style={{ padding: "9px 10px" }}>
                      <DocStatusPill status={d.status} />
                    </td>
                    <td style={{ padding: "9px 10px" }}>
                      {/* The row stays and the button goes (KEY-537): this reader holds list rights, so
                          the file's existence is already disclosed and withholding the row would tell them
                          less — while a button that 403s would tell them worse. `!== false` because absent
                          means an older server that never answered, where the pre-KEY-537 behaviour is to
                          offer it and let the server decide. */}
                      {d.status === "Clean" && d.canDownload !== false && (
                        <button onClick={() => void download(d)} title="Download" aria-label={`Download ${d.fileName}`} style={iconBtn}>
                          <Icon name="download" size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* The footer is shown even on a single page: "1–7 of 7" is the answer to "is this all of them?",
              which is worth more than the few pixels saved by hiding it. */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 12 }}>
            <span style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12.5, color: "var(--text-faint)" }}>
              <span>
                Showing {firstOnPage}–{lastOnPage} of {total}
                {selected.size > 0 && ` · ${selected.size} selected`}
              </span>
              {/* The key to the Location column's icons. Lives with the row count rather than in the header
                  so the table keeps its lines, and reads left-to-right in the order the dial is walked. */}
              <span aria-label="Key to the location icons" style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px" }}>
                {LOCATION_KINDS.map((k) => (
                  <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <Icon name={LOCATION_ICON[k]} size={12} />
                    {locationLabel[k]}
                  </span>
                ))}
              </span>
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="kd-focusable"
                disabled={page.page <= 1}
                onClick={() => setPageNumber((n) => Math.max(1, n - 1))}
                style={pagerBtn(page.page <= 1)}
              >
                <Icon name="chevLeft" size={14} />
                Previous
              </button>
              <button
                type="button"
                className="kd-focusable"
                disabled={!hasMore}
                onClick={() => setPageNumber((n) => n + 1)}
                style={pagerBtn(!hasMore)}
              >
                Next
                <Icon name="chevRight" size={14} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const selectStyle = {
  font: "inherit", fontSize: 12.5, fontWeight: 600, padding: "5px 8px", borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)",
  maxWidth: 220,
} as const;

function pagerBtn(disabled: boolean) {
  return {
    font: "inherit", fontSize: 12.5, fontWeight: 700, padding: "5px 10px", borderRadius: "var(--radius-sm)",
    border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)",
    cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.45 : 1,
    display: "inline-flex", alignItems: "center", gap: 4,
  } as const;
}
